import * as PIXI from 'pixi.js';
import { collidersConfig, gameConfig } from '@/config';
import { groundItemsConfig } from '@/config/GroundItemsConfig';
import { Collider, Item, type NPC, Player } from '@/entities';
import { PolygonCollider } from '@/entities/PolygonCollider';
import { AssetManager } from '@/managers';
import { Camera, InputManager, SoundManager, StoryManager } from '@/systems';
import type { DialogTree } from '@/types';
import {
  AffectionDisplay,
  DayDisplay,
  DialogSystem,
  EndingScreen,
  InventoryDisplay,
  SplashScreen,
  TutorialModal,
  VolumeControl,
} from '@/ui';
import { VignetteDisplayArc } from '@/ui/VignetteDisplayArc.ts';
import { VignetteDisplayRadial } from '@/ui/VignetteDisplayRadial.ts';
import { ColliderEditorTool } from '@/utils/ColliderEditorTool';
import { World } from './World';

import 'pixi.js/basis';
import 'pixi.js/ktx';
import 'pixi.js/ktx2';
import { VignetteEyeBlink } from '@/ui/VignetteEyeBlink';

export class Game {
  private app: PIXI.Application;
  private inputManager!: InputManager;
  private player!: Player;
  private world!: World;
  private camera!: Camera;
  private colliders: Collider[] = [];
  private polygonColliders: PolygonCollider[] = [];
  private currentNPCs: NPC[] = [];
  private currentItems: Item[] = [];
  private storyManager!: StoryManager;
  private dialogSystem!: DialogSystem;
  private dayDisplay!: DayDisplay;
  private vignetteDisplayArc!: VignetteDisplayArc;
  private vignetteDisplayRadial!: VignetteDisplayRadial;
  private eyeBlinkVignetteDisplay!: VignetteEyeBlink;
  private affectionDisplay!: AffectionDisplay;
  private inventoryDisplay!: InventoryDisplay;
  private endingScreen!: EndingScreen;
  private splashScreen!: SplashScreen;
  private assetManager!: AssetManager;
  private soundManager!: SoundManager;
  private tutorialModal!: TutorialModal;
  private volumeControl!: VolumeControl;
  private colliderEditorTool!: ColliderEditorTool;
  private lastTime = 0;
  private collidersVisible = false; // Track collider visibility state
  // private lastPlayerPosition = { x: 0, y: 0 };

  private static gameInstance: Game;

  public static getInstance() {
    return Game.gameInstance;
  }

  public this() {
    return this.app;
  }

  public getDayDisplay() {
    return this.dayDisplay;
  }

  public getVignetteDisplayRadial() {
    return this.vignetteDisplayRadial;
  }

  public getVignetteDisplayArc() {
    return this.vignetteDisplayArc;
  }

  public setVignetteDisplayBPM(bpm: number) {
    this.vignetteDisplayArc.setBPM(bpm);
    this.vignetteDisplayRadial.setBPM(bpm);
  }

  public startEyeBlink() {
    this.eyeBlinkVignetteDisplay.startBlink();
  }

  constructor() {
    // Initialize PIXI Application
    this.app = new PIXI.Application();
    Game.gameInstance = this;

    // Initialize the app asynchronously and set up the game
    this.initializeApp();
  }

  /**
   * Calculates screen dimensions, swapping width/height if in mobile portrait mode
   * to match the CSS rotation.
   */
  private getScreenDimensions(): { width: number; height: number } {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  /**
   * Updates the game scale (camera zoom) based on screen dimensions
   * Ensures the view never exceeds world bounds (no grey bars)
   */
  private updateGameScale(screenWidth: number, screenHeight: number): void {
    if (!this.camera) return;

    const targetHeight = 720;
    const minZoom = 0.5;
    const maxZoom = 1.0;

    // Default scale based on 720p target
    let gameScale = screenHeight / targetHeight;
    gameScale = Math.max(minZoom, Math.min(maxZoom, gameScale));

    // Check world bounds limit if world is loaded
    if (this.world) {
      const { width: worldW, height: worldH } = this.world.getActualWorldDimensions();
      if (worldW > 0 && worldH > 0) {
        // Calculate minimum scale needed to cover the screen
        // We need: scale * worldW >= screenWidth  AND  scale * worldH >= screenHeight
        const minScaleX = screenWidth / worldW;
        const minScaleY = screenHeight / worldH;
        const minWorldScale = Math.max(minScaleX, minScaleY);

        // Ensure we don't zoom out too much (scale shouldn't be lower than minWorldScale)
        gameScale = Math.max(gameScale, minWorldScale);
      }
    }

    if (InputManager.getInstance().isMobile()) {
      gameScale *= 1.3; // Zoom in 30% for mobile
    }

    // Apply scale
    this.camera.container.scale.set(gameScale);
    this.camera.updateViewBounds(screenWidth / gameScale, screenHeight / gameScale);
  }

  private async initializeApp(): Promise<void> {
    // Initialize PIXI Application with v8 async init
    // Determine correct dimensions (swap if mobile portrait to match CSS rotation)
    const { width, height } = this.getScreenDimensions();

    // Initialize PIXI Application with v8 async init
    await this.app.init({
      width: width,
      height: height,
      backgroundColor: gameConfig.renderer.backgroundColor,
      // resizeTo: window, // REMOVED: We manage resize manually to handle rotation swap
      resolution: gameConfig.renderer.resolution,
      autoDensity: gameConfig.renderer.autoDensity,
      antialias: gameConfig.renderer.antialias,
    });

    // Add canvas to DOM
    const gameContainer = document.getElementById('gameContainer');
    if (gameContainer) {
      gameContainer.appendChild(this.app.canvas);
    }

    // Initialize asset manager
    this.assetManager = AssetManager.getInstance();

    // Create and show splash screen with loading functionality
    this.splashScreen = new SplashScreen(this.app.screen.width, this.app.screen.height);
    this.app.stage.addChild(this.splashScreen.getContainer());

    // Show loading state and preload all assets
    this.splashScreen.showLoading();
    await this.preloadAssets();

    // Initialize sound manager early (sounds are now preloaded)
    this.soundManager = SoundManager.getInstance();

    // Auto-start game after loading
    this.splashScreen.onStart(() => {
      // Initialize audio context (audio will remain muted until user interaction)
      this.initializeAudioContext()
        .then(async () => {
          // Initialize the rest of the game
          return await this.initializeGame();
        })
        .then(() => {
          // Hide loading UI and splash screen once game is initialized
          this.splashScreen.hideLoading();
          this.splashScreen.hide();

          // Setup event listeners (includes listener for unmuting audio)
          this.setupEventListeners();

          // Start game loop
          this.app.ticker.add(this.gameLoop.bind(this));

          // Start game loop
          this.app.ticker.add(this.gameLoop.bind(this));

          // Show interaction button and joystick for mobile
          InputManager.getInstance().setInteractButtonVisible(false);
          InputManager.getInstance().setMobileControlsVisible(true);

          // Background music is now handled by StoryManager.setDay(1) call
        })
        .catch((error: Error) => {
          console.error('Game initialization error:', error);
          // Update loading text to show error
          this.splashScreen.updateLoadingText('Error initializing game. Please try again.');

          // Hide spinner but keep the text visible
          this.splashScreen.hideLoading();
        });
    });
  }

  // initializeAudio() method removed as it's no longer used
  // Game now uses the simpler initializeAudioContext() method instead

  /**
   * Preload all assets (images and audio) with progress tracking
   */
  private async preloadAssets(): Promise<void> {
    console.log('Starting asset preloading...');

    // Start ticker for splash screen animation
    const updateSplashScreen = () => {
      this.splashScreen.update();
    };
    this.app.ticker.add(updateSplashScreen);

    try {
      await this.assetManager.preloadAllAssets(progress => {
        this.splashScreen.updateProgress(progress);
      });
      console.log('Asset preloading completed successfully');
    } catch (error) {
      console.error('Asset preloading failed:', error);
      // Continue anyway - some assets might still work
    } finally {
      // Stop the splash screen animation ticker
      this.app.ticker.remove(updateSplashScreen);
      // Hide loading state
      this.splashScreen.hideLoading();
    }
  }

  /**
   * Initialize audio context (simplified version for user interaction)
   * Assets are already preloaded, this just ensures audio context is ready
   * Audio remains muted until the first user interaction
   */
  private async initializeAudioContext(): Promise<void> {
    try {
      console.log('Creating audio context without resuming (muted)...');
      // Use true for skipResume to create but not resume the context
      await this.soundManager.resumeAudioContext(true);
      console.log('Audio context created successfully (suspended, muted)');

      // Ensure audio remains muted until user interaction
      if (!this.soundManager.isMuted()) {
        this.soundManager.setMute(true);
      }
    } catch (error) {
      console.error('Failed to initialize audio context:', error);
      // Show error message to user but continue with game
      const errorDiv = document.createElement('div');
      errorDiv.style.position = 'fixed';
      errorDiv.style.bottom = '10px';
      errorDiv.style.left = '10px';
      errorDiv.style.backgroundColor = 'rgba(255, 0, 0, 0.7)';
      errorDiv.style.color = 'white';
      errorDiv.style.padding = '10px';
      errorDiv.style.borderRadius = '5px';
      errorDiv.style.zIndex = '1000';
      errorDiv.textContent = 'Audio initialization failed. Sounds may not play.';
      document.body.appendChild(errorDiv);

      // Remove after 5 seconds
      setTimeout(() => {
        if (document.body.contains(errorDiv)) {
          document.body.removeChild(errorDiv);
        }
      }, 5000);
    }
  }

  private async initializeGame(): Promise<void> {
    this.inputManager = new InputManager(this.app.screen.width, this.app.screen.height);

    // Initialize day display
    this.dayDisplay = new DayDisplay();

    // Initialize Story Manager
    this.storyManager = StoryManager.getInstance();

    // Explicitly set day 1 to trigger the day music
    // Moved to after player creation to ensure player config (position, facing) is applied
    // this.storyManager.setDay(1);

    // Create world (larger than screen for open-world feel)
    const worldWidth = gameConfig.world.width;
    const worldHeight = gameConfig.world.height;
    this.world = new World(worldWidth, worldHeight);

    // Wait for world background to load
    await this.world.waitForBackgroundLoad();

    // This doesn't initialize object layers automatically anymore

    // Get actual world dimensions after background is loaded
    const { width: actualWorldWidth, height: actualWorldHeight } =
      this.world.getActualWorldDimensions();

    // Create camera with actual world bounds
    this.camera = new Camera(
      new PIXI.Rectangle(0, 0, actualWorldWidth, actualWorldHeight),
      this.app.screen.width,
      this.app.screen.height,
    );

    // Create player at center of actual world
    this.player = new Player(1850, 1550);

    // Set player reference in StoryManager for teleportation functionality
    this.storyManager.setPlayer(this.player);

    // Initialize day 1 explicitly now that player is set, to apply day config (position, facing, music)
    this.storyManager.setDay(1);

    // Create colliders (walls) using actual world dimensions
    this.createColliders(actualWorldWidth, actualWorldHeight);

    // Load polygon colliders from config
    this.loadPolygonColliders();

    // Initialize collider editor tool
    this.colliderEditorTool = new ColliderEditorTool({
      camera: {
        container: this.camera.container,
        getWorldPosition: (screenX: number, screenY: number) => {
          // Convert screen coordinates to world coordinates
          const worldX = screenX - this.camera.container.x;
          const worldY = screenY - this.camera.container.y;
          return { x: worldX, y: worldY };
        },
      },
      onSave: polygons => {
        console.log('Polygons saved via callback:', polygons);
      },
    });

    // Initialize NPCs using StoryManager
    this.currentNPCs = this.storyManager.initializeNPCs();

    // Initialize ground items
    this.initializeGroundItems();

    // Initialize dialog system
    this.dialogSystem = new DialogSystem(this.app.screen.width, this.app.screen.height);
    this.dialogSystem.setGame(this);

    // Initialize vignette
    this.vignetteDisplayArc = new VignetteDisplayArc(this.app.screen.width, this.app.screen.height);
    this.vignetteDisplayRadial = new VignetteDisplayRadial(
      this.app.screen.width,
      this.app.screen.height,
    );
    this.eyeBlinkVignetteDisplay = new VignetteEyeBlink(
      this.app.screen.width,
      this.app.screen.height,
    );

    // Initialize affection display
    this.affectionDisplay = new AffectionDisplay();

    // Initialize inventory display
    this.inventoryDisplay = new InventoryDisplay(this.app.screen.width, this.app.screen.height);

    // Initialize ending screen
    this.endingScreen = new EndingScreen(this.app.screen.width, this.app.screen.height);

    this.tutorialModal = new TutorialModal({
      x: 10,
      y: 10,
    });

    // Initialize volume control under the tutorial button
    this.volumeControl = new VolumeControl({
      x: 10,
      y: 10 + this.tutorialModal.getButtonHeight() + this.tutorialModal.getButtonGap(),
    });

    // Set camera to follow player
    this.camera.setTarget(this.player.getPosition());
    // Snap immediately on first load so camera doesn't glide from origin.
    this.camera.snapToTarget();

    // Add world and player to camera
    // World has lowest z-index (0)
    this.camera.addToWorld(this.world.container, 0);
    // Player has z-index of 2 by default
    this.camera.addToWorld(this.player.sprite, 2);
    // Add player collider sprite (for debugging, z-index 3 to be above player)
    this.camera.addToWorld(this.player.getColliderSprite(), 3);

    // Add colliders to camera
    for (const collider of this.colliders) {
      // Colliders have z-index of 1 (below player)
      this.camera.addToWorld(collider.sprite, 1);
    }

    // Add NPCs to camera
    for (const npc of this.currentNPCs) {
      // NPCs have lower z-index than player (1 vs 2) so player renders on top
      this.camera.addToWorld(npc.sprite, 1);
      // Add NPC UI container at high z-index so name text and speech bubble render above world elements
      this.camera.addToWorld(npc.uiContainer, 100);

      // Set up click callback for the speech bubble
      npc.setOnInteractionClick(() => {
        if (!this.dialogSystem.isActive()) {
          const playerPosition = this.player.getPosition();
          const interactionResult = this.storyManager.handleNPCInteraction(playerPosition);

          if (interactionResult) {
            if (
              typeof interactionResult === 'object' &&
              'type' in interactionResult &&
              interactionResult.type === 'repeat'
            ) {
              // Show simple repeat dialog with images
              this.dialogSystem
                .showSimpleDialog(
                  interactionResult.speaker,
                  interactionResult.text,
                  npc.name,
                  interactionResult.characterImage,
                  interactionResult.backgroundImage,
                  interactionResult.isEnd,
                )
                .catch(error => {
                  console.error('Failed to show simple dialog:', error);
                });
            } else {
              // Show full dialog tree for first interaction
              this.dialogSystem
                .showDialogTree(interactionResult as DialogTree, npc.name)
                .catch(error => {
                  console.error('Failed to show dialog tree:', error);
                });
            }
          }
        }
      });
    }

    // Initialize object layers - these will be added directly to the camera
    // with their configured z-indices for proper layering
    await this.world.initializeObjectLayersWithCamera(this.camera);

    // Add camera to stage
    this.app.stage.addChild(this.camera.container);

    // Add dialog system to stage (not to camera, so it stays fixed on screen)
    this.app.stage.addChild(this.dialogSystem.getContainer());
    this.dialogSystem.startWalkingAnimation(this.app);

    // Update day display position to ensure it's in the top right corner
    this.dayDisplay.updatePosition(this.app.screen.width, this.app.screen.height);

    // Add affection display to stage (not to camera, so it stays fixed on screen)
    this.app.stage.addChild(this.affectionDisplay.getContainer());

    // Disable affection display as requested
    this.affectionDisplay.setVisible(false);

    // Add ending screen to stage (highest priority, not to camera)
    this.app.stage.addChild(this.endingScreen.getContainer());

    // Add collider editor instruction text to stage
    this.app.stage.addChild(this.colliderEditorTool.getInstructionText());

    // Add Vignette
    this.app.stage.addChild(this.vignetteDisplayArc.getContainer());
    this.app.stage.addChild(this.vignetteDisplayRadial.getContainer());
    this.app.stage.addChild(this.eyeBlinkVignetteDisplay.getContainer());
    this.vignetteDisplayArc.startAnimation(this.app);
    this.vignetteDisplayRadial.startAnimation(this.app);
    this.eyeBlinkVignetteDisplay.initializeAnimation(this.app);

    // Add inventory display to stage (not to camera, so it stays fixed on screen)
    this.app.stage.addChild(this.inventoryDisplay.getContainer());

    // Add volume control to stage
    this.app.stage.addChild(this.volumeControl.getContainer());
    this.volumeControl.show();
    this.volumeControl.updateScreenSize(this.app.screen.width, this.app.screen.height);

    // Add day display to stage (not to camera, so it stays fixed on screen)
    this.app.stage.addChild(this.dayDisplay.getContainer());
    this.app.stage.addChild(this.inputManager.getButton());
    // this.app.stage.addChild(this.inputManager.getFullScreenButton())

    // Add tutorial modal last so it overlays on top of everything else (including Prolog)
    this.app.stage.addChild(this.tutorialModal.getContainer());
    this.tutorialModal.showButton();
    this.tutorialModal.show(); // Automatically trigger the tutorial popup on start
    this.tutorialModal.updateScreenSize(this.app.screen.width, this.app.screen.height);

    // Set up PIXI UI detection to prevent joystick activation when clicking UI elements
    this.inputManager.setPixiUICheckCallback((x: number, y: number) => {
      return this.isOverPixiUI(x, y);
    });

    // Check and show initial day dialog for the starting day (with a small delay)
    setTimeout(() => {
      this.checkAndShowInitialDayDialog();
    }, 100);

    // loadKTX2.config = {
    //   transcoderPath: 'https://cdn.jsdelivr.net/npm/pixi.js@8.x/dist/browser/basis/'
    // };
  }

  /**
   * Check if screen coordinates are over a PIXI UI element
   * This prevents joystick activation when clicking UI elements
   */
  private isOverPixiUI(screenX: number, screenY: number): boolean {
    // Check if position is over volume control
    if (this.volumeControl?.getContainer().visible) {
      const bounds = this.volumeControl.getContainer().getBounds();
      if (
        screenX >= bounds.x &&
        screenX <= bounds.x + bounds.width &&
        screenY >= bounds.y &&
        screenY <= bounds.y + bounds.height
      ) {
        return true;
      }
    }

    if (this.tutorialModal?.getContainer().visible) {
      const bounds = this.tutorialModal.getContainer().getBounds();
      if (
        screenX >= bounds.x &&
        screenX <= bounds.x + bounds.width &&
        screenY >= bounds.y &&
        screenY <= bounds.y + bounds.height
      ) {
        return true;
      }
    }

    // Check if position is over dialog system
    if (this.dialogSystem?.getContainer().visible) {
      const bounds = this.dialogSystem.getContainer().getBounds();
      if (
        screenX >= bounds.x &&
        screenX <= bounds.x + bounds.width &&
        screenY >= bounds.y &&
        screenY <= bounds.y + bounds.height
      ) {
        return true;
      }
    }

    // Check if position is over ending screen
    if (this.endingScreen?.getContainer().visible) {
      const bounds = this.endingScreen.getContainer().getBounds();
      if (
        screenX >= bounds.x &&
        screenX <= bounds.x + bounds.width &&
        screenY >= bounds.y &&
        screenY <= bounds.y + bounds.height
      ) {
        return true;
      }
    }

    // Check if position is over splash screen
    if (this.splashScreen?.getContainer().visible) {
      const bounds = this.splashScreen.getContainer().getBounds();
      if (
        screenX >= bounds.x &&
        screenX <= bounds.x + bounds.width &&
        screenY >= bounds.y &&
        screenY <= bounds.y + bounds.height
      ) {
        return true;
      }
    }

    // Check if position is over inventory display
    if (this.inventoryDisplay?.getContainer().visible) {
      const bounds = this.inventoryDisplay.getContainer().getBounds();
      if (
        screenX >= bounds.x &&
        screenX <= bounds.x + bounds.width &&
        screenY >= bounds.y &&
        screenY <= bounds.y + bounds.height
      ) {
        return true;
      }
    }

    // Check if position is over any NPC speech bubble
    const npcs = this.storyManager.getNPCs();
    for (const npc of npcs) {
      const bounds = npc.uiContainer.getBounds();
      if (bounds.width > 0 && bounds.height > 0) {
        if (
          screenX >= bounds.x &&
          screenX <= bounds.x + bounds.width &&
          screenY >= bounds.y &&
          screenY <= bounds.y + bounds.height
        ) {
          return true;
        }
      }
    }

    return false;
  }

  private createColliders(worldWidth: number, worldHeight: number): void {
    // Create some walls around the world border
    this.colliders.push(
      // Top wall
      new Collider({
        x: 0,
        y: -collidersConfig.walls.wallThickness,
        width: worldWidth,
        height: collidersConfig.walls.wallThickness,
        color: collidersConfig.walls.wallColor,
        visible: false,
      }),
      // Bottom wall
      new Collider({
        x: 0,
        y: worldHeight,
        width: worldWidth,
        height: collidersConfig.walls.wallThickness,
        color: collidersConfig.walls.wallColor,
        visible: false,
      }),
      // Left wall
      new Collider({
        x: -collidersConfig.walls.wallThickness,
        y: 0,
        width: collidersConfig.walls.wallThickness,
        height: worldHeight,
        color: collidersConfig.walls.wallColor,
        visible: false,
      }),
      // Right wall
      new Collider({
        x: worldWidth,
        y: 0,
        width: collidersConfig.walls.wallThickness,
        height: worldHeight,
        color: collidersConfig.walls.wallColor,
        visible: false,
      }),
    );

    // Removed obstacle blocks - now only world boundary walls remain
  }

  private loadPolygonColliders(): void {
    // Load polygon colliders from config
    for (const polygonConfig of collidersConfig.polygons) {
      const polygon = new PolygonCollider({
        points: polygonConfig.points,
        color: polygonConfig.color,
        visible: polygonConfig.visible,
      });
      this.polygonColliders.push(polygon);
      // Polygon colliders have z-index of 1 (below player)
      this.camera.addToWorld(polygon.sprite, 1);
    }
    console.log(`Loaded ${this.polygonColliders.length} polygon colliders from config`);
  }

  private setupEventListeners(): void {
    // Handle window resize
    window.addEventListener('resize', () => {
      // Logic inside listener
      const { width, height } = this.getScreenDimensions();
      console.log(
        `Resize detected: ${width}x${height} (Screen: ${window.innerWidth}x${window.innerHeight})`,
      );
      this.app.renderer.resize(width, height);

      this.updateGameScale(width, height);

      this.dialogSystem.updateScreenSize(width, height);
      this.dayDisplay.updatePosition(width, height);
      this.affectionDisplay.updatePosition(width, height);
      this.inventoryDisplay.updatePosition(width, height);
      this.inputManager.updateScreenSize(width, height);
      this.endingScreen.updateScreenSize(width, height);
      this.splashScreen.updateScreenSize(width, height);
      this.tutorialModal.updateScreenSize(width, height);
      this.volumeControl.updateScreenSize(width, height);

      this.app.render();
    });

    // Determine correct dimensions (swap if mobile portrait to match CSS rotation)
    const { width, height } = this.getScreenDimensions();

    // Initial Camera Zoom/Scale Trigger
    this.updateGameScale(width, height);
    // Re-snap after scale update to prevent visible camera drift on enter.
    this.camera.snapToTarget();

    // Prevent context menu on mobile
    window.addEventListener('contextmenu', e => {
      e.preventDefault();
    });

    // Listen for any user interaction to unmute audio
    const userInteractionEvents = ['click', 'touchstart', 'keydown', 'pointerdown'];

    const handleFirstInteraction = async () => {
      console.log('First user interaction detected, unmuting audio and resuming context');

      try {
        // First, resume the AudioContext since we now have user interaction
        await this.soundManager.resumeAudioContext(false); // false = do resume

        // Then unmute audio
        if (this.soundManager.isMuted()) {
          this.soundManager.setMute(false);
          console.log('Audio unmuted, forcing volume control icon update');

          // Update the UI to reflect the unmuted state
          // Remove the old volume control from the stage
          this.app.stage.removeChild(this.volumeControl.getContainer());

          // Create a new volume control
          this.volumeControl = new VolumeControl({
            x: 10,
            y: 10 + this.tutorialModal.getButtonHeight() + this.tutorialModal.getButtonGap(),
          });

          // Add it back to the stage
          this.app.stage.addChild(this.volumeControl.getContainer());
          this.volumeControl.show();
          this.volumeControl.updateScreenSize(this.app.screen.width, this.app.screen.height);
        }

        // Remove all event listeners once audio is unmuted
        userInteractionEvents.forEach(eventType => {
          document.removeEventListener(eventType, handleFirstInteraction);
        });

        console.log('Audio successfully resumed and unmuted after user interaction');
      } catch (error) {
        console.error('Failed to resume audio on user interaction:', error);
      }
    };

    // Add event listeners for first interaction
    userInteractionEvents.forEach(eventType => {
      document.addEventListener(eventType, handleFirstInteraction);
    });

    // Listen for game victory and game over events from dialogs
    window.addEventListener('gameVictory', (event: Event) => {
      const detail = (event as CustomEvent).detail;
      console.log('Victory triggered by dialog:', detail);

      // Hide mobile controls
      InputManager.getInstance().setMobileControlsVisible(false);
      InputManager.getInstance().setInteractButtonVisible(false);

      this.showEndingScreen(
        'victory',
        '🎉 VICTORY! 🎉',
        detail.reason || 'You have achieved victory through your choices!',
      );

      //ending music
      const finalReport = this.storyManager.getFinalReport();
      const endingGrade = finalReport.endingGrade;

      this.soundManager.stopMusic();

      if (endingGrade === 'ENDING_S_SECRET_ENDING') {
        this.soundManager.playMusic('music_secret_ending');
      } else if (endingGrade === 'ENDING_A_GOOD_ENDING') {
        this.soundManager.playMusic('music_happy_a');
      } else if (endingGrade === 'ENDING_B_STILL_SURVIVE_ENDING') {
        this.soundManager.playMusic('music_happy_b');
      } else {
        // Default victory music
        this.soundManager.playMusic('music_happy_a');
      }
    });

    window.addEventListener('gameOver', (event: Event) => {
      const detail = (event as CustomEvent).detail;
      console.log('Game over triggered by dialog:', detail);

      // Hide mobile controls
      InputManager.getInstance().setMobileControlsVisible(false);
      InputManager.getInstance().setInteractButtonVisible(false);

      let finalReport = StoryManager.getInstance().getFinalReport()
      const gameData = {
        source: 'my-pixi-game',
        type: 'GAME_OVER',
        payload: {
          ...finalReport
        }
      };

      // Send message to parent wrapper
      if (window.parent !== window) {
        window.parent.postMessage(gameData, '*');
        console.log("Message sent to Wrapper:", gameData);
      }

      // Play game over music
      const endingGrade = finalReport.endingGrade;

      this.soundManager.stopMusic();

      if (endingGrade === 'ENDING_D_FAIL_SELF_OR_ELSA_ENDING') {
        // Sad melody for Elsa failing
        this.soundManager.playMusic('music_musicBox');
      } else {
        // Standard game over theme
        this.soundManager.playMusic('music_bad_ending');
      }
    });

    // Listen for day changes to update NPCs
    this.storyManager.addDayEventListener('dayChanged', (day: number) => {
      console.log(`Day changed to ${day}, updating NPCs...`);
      this.updateNPCsForNewDay();

      // Snap camera to player immediately to avoid swipe animation
      // We need to wait a brief moment for the player position to be fully updated
      // but configured to happen on the same frame logically
      if (this.camera) {
        this.camera.setTarget(this.player.getPosition());
        this.camera.snapToTarget();
      }

      // // Play day change sound
      // this.soundManager.play('sfx_dayChange');

      // Change background music based on day number (odd days for night, even for day)
      // const currentDay = this.storyManager.getCurrentDay();
      // if (currentDay % 2 === 0) {
      //   this.soundManager.playMusic('music_dayAmbience', 2000);
      // } else {
      //   this.soundManager.playMusic('music_nightAmbience', 2000);
      // }

      // Check and show initial day dialog with a small delay to ensure dialog system is ready
      setTimeout(() => {
        this.checkAndShowInitialDayDialog();
      }, 200);
    });

    // Add keyboard shortcut for day advancement (for testing - remove in production)
    window.addEventListener('keydown', e => {
      // Debug: Log Ctrl key combinations
      if (e.ctrlKey) {
        console.log(`Ctrl+${e.key} pressed`);
      }

      if (e.key === '>' && e.ctrlKey) {
        // Ctrl+N to advance day
        e.preventDefault();
        this.storyManager.nextDay();
      } else if (e.key === '<' && e.ctrlKey) {
        // Ctrl+P to go to previous day
        e.preventDefault();
        // this.storyManager.previousDay();
      } else if (e.key === 'W' && e.ctrlKey) {
        // Ctrl+W to trigger win condition (testing)
        e.preventDefault();
        this.storyManager.setAffection(20);
        this.storyManager.setHostility(0);
        // Unlock some special dialogs for testing
        for (let i = 1; i <= 5; i++) {
          this.storyManager.unlockSpecialDialog(`test_dialog_${i}`, 'TestNPC', 'special');
        }
        console.log('Win condition triggered for testing');
      } else if (e.key === 'L' && e.ctrlKey) {
        // Ctrl+L to trigger lose condition (testing)
        e.preventDefault();
        this.storyManager.setHostility(25);
        console.log('Lose condition triggered for testing');
      } else if ((e.key === 'M' || e.key === 'm') && e.ctrlKey) {
        // Ctrl+M to test postMessage to wrapper (remove in production)
        e.preventDefault();
      } else if ((e.key === 'E' || e.key === 'e') && e.ctrlKey) {
        // Ctrl+E to toggle collider editor
        e.preventDefault();
        if (this.colliderEditorTool) {
          const isActive = this.colliderEditorTool.toggle();
          console.log(`Collider editor ${isActive ? 'activated' : 'deactivated'}`);
        } else {
          console.error('Collider editor tool not initialized');
        }
      } else if ((e.key === 'I' || e.key === 'i') && e.ctrlKey) {
        // Ctrl+I to print player position
        e.preventDefault();
        const playerPos = this.player.getPosition();
        console.log('='.repeat(50));
        console.log('PLAYER POSITION:');
        console.log(`  x: ${playerPos.x}`);
        console.log(`  y: ${playerPos.y}`);
        console.log('Copy format for NPCConfig:');
        console.log(`  { x: ${playerPos.x}, y: ${playerPos.y} }`);
        console.log('='.repeat(50));
      } else if (this.colliderEditorTool?.isEditorActive()) {
        // Forward keyboard input to editor when it's active
        this.colliderEditorTool.handleKeyPress(e.key);
      }
    });

    // Add mouse event listeners for collider editor
    window.addEventListener('mousemove', e => {
      if (this.colliderEditorTool?.isEditorActive()) {
        this.colliderEditorTool.handleMouseMove(e.clientX, e.clientY);
      }
    });

    window.addEventListener('mousedown', () => {
      if (this.colliderEditorTool?.isEditorActive()) {
        this.colliderEditorTool.handleMouseDown();
      }
    });

    window.addEventListener('mouseup', e => {
      if (this.colliderEditorTool?.isEditorActive()) {
        this.colliderEditorTool.handleMouseUp(e.clientX, e.clientY);
      }
    });
  }

  /**
   * Initialize ground items from config
   */
  private initializeGroundItems(): void {
    // Create items from config
    for (const itemConfig of groundItemsConfig) {
      const item = new Item(itemConfig);
      this.currentItems.push(item);

      // Add item to camera (same z-index as NPCs)
      this.camera.addToWorld(item.sprite, 1);

      // Set up click callback for the item
      item.setOnInteractionClick(() => {
        if (!this.dialogSystem.isActive()) {
          const playerPosition = this.player.getPosition();
          if (item.canInteract(playerPosition)) {
            this.handleItemPickup(item);
          }
        }
      });
    }

    console.log(`Initialized ${this.currentItems.length} ground items`);
  }

  /**
   * Handle item pickup
   */
  private handleItemPickup(item: Item): void {
    // Show item pickup dialog
    this.dialogSystem
      .showItemPickupDialog(item.itemId, item.quantity)
      .then(() => {
        // Remove item from world after pickup
        this.removeItem(item);
      })
      .catch(error => {
        console.error('Failed to show item pickup dialog:', error);
      });
  }

  /**
   * Remove an item from the game world
   */
  private removeItem(item: Item): void {
    const index = this.currentItems.indexOf(item);
    if (index > -1) {
      this.currentItems.splice(index, 1);
      item.destroy();
      console.log(`Removed item ${item.itemId} from world`);
    }
  }

  /**
   * Update all items (for animations, etc.)
   */
  private updateItems(
    deltaTime: number,
    playerPosition: { x: number; y: number },
  ): {
    playerNearItem: boolean;
    closestItem: Item | null;
  } {
    let playerNearItem = false;
    let closestItem: Item | null = null;
    let closestDistance = Infinity;

    // First pass: update all items and find closest
    for (const item of this.currentItems) {
      item.update(deltaTime, playerPosition);

      if (item.canInteract(playerPosition)) {
        playerNearItem = true;

        // Calculate distance to find closest item
        const dx = playerPosition.x - item.getPosition().x;
        const dy = playerPosition.y - item.getPosition().y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = item;
        }
      }
    }

    // Second pass: set focus only on the closest item
    for (const item of this.currentItems) {
      item.setFocused(item === closestItem);
    }

    return { playerNearItem, closestItem };
  }

  /**
   * Check and show initial day dialog if needed
   */
  private checkAndShowInitialDayDialog(): void {
    const currentDay = this.storyManager.getCurrentDay();
    const shouldShow = this.storyManager.shouldShowInitialDayDialog();
    const dialogActive = this.dialogSystem.isActive();
    const initialDialog = this.storyManager.getInitialDayDialog();

    console.log(`Checking initial dialog for day ${currentDay}:`);
    console.log(`- Should show: ${shouldShow}`);
    console.log(`- Dialog active: ${dialogActive}`);
    console.log(`- Has initial dialog: ${!!initialDialog}`);

    // Only show initial day dialog if not already shown and dialog system is not active
    if (shouldShow && initialDialog) {
      console.log(`Showing initial dialog for day ${currentDay}`);
      this.dialogSystem
        .showInitialDayDialog(initialDialog)
        .then(() => {
          // Mark as shown after dialog is displayed
          this.storyManager.markInitialDayDialogShown();
          console.log(`Initial dialog for day ${currentDay} marked as shown`);
        })
        .catch(error => {
          console.error('Failed to show initial day dialog:', error);
        });
    } else {
      console.log(
        `Not showing initial dialog for day ${currentDay} (shouldShow: ${shouldShow}, dialogActive: ${dialogActive}, hasDialog: ${!!initialDialog})`,
      );
    }
  }

  private gameLoop(): void {
    const currentTime = Date.now();
    const deltaTime = this.lastTime === 0 ? 0 : (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    // Get input state
    const inputState = this.inputManager.getInputState();
    const playerPosition = this.player.getPosition();
    const isTutorialOpen = this.tutorialModal?.isVisible() ?? false;

    // // Initialize lastPlayerPosition on first frame if needed
    // if (this.lastTime === 0) {
    //   this.lastPlayerPosition = this.player.getPosition();
    // }

    // Only update game logic if ending screen is not showing
    if (!this.endingScreen.isShowing()) {
      // Only update player if dialog is not active
      if (!this.dialogSystem.isActive() && !isTutorialOpen) {
        // Combine static colliders, polygon colliders, and NPC colliders for player collision detection
        const npcs = this.storyManager.getNPCs();
        const allColliders = [
          ...this.colliders,
          ...this.polygonColliders,
          ...npcs.map(npc => npc.getCollider()),
        ];
        this.player.update(deltaTime, inputState, this.world.bounds, allColliders);

        // // Get current position after update
        // const newPosition = this.player.getPosition();

        // // Play footstep sounds when player is moving
        // const isMovingNow =
        //   newPosition.x !== this.lastPlayerPosition.x ||
        //   newPosition.y !== this.lastPlayerPosition.y;

        // if (isMovingNow) {
        //   // Only play the sound occasionally to avoid sound spam
        //   if (Math.random() < 0.05) {
        //     // Adjust probability for desired frequency
        //     this.soundManager.play('sfx_footsteps', { volume: 0.4 });
        //   }
        // }

        // // Store current position for next frame comparison
        // this.lastPlayerPosition = { ...newPosition };

        // Note: Collision sound logic would ideally need Player.ts to be updated to track collisions
        // We could implement this in the future
      }

      this.world.update(deltaTime);

      // Update NPCs using StoryManager and get interaction info
      const { closestNPC } = this.storyManager.updateNPCs(deltaTime, playerPosition);

      // Update items and get interaction info
      const { closestItem } = this.updateItems(deltaTime, playerPosition);

      // Check if interaction button should be visible (if player is near interactable entity)
      let interactionAvailable = false;

      const isDialogActive = this.dialogSystem.isActive();
      const isEndingShowing = this.endingScreen.isShowing();
      const isSplashVisible = this.splashScreen?.getContainer()?.visible ?? false;
      const isInventoryDisplaying = this.inventoryDisplay.getExpanded();

      const shouldInteractBtnBeVisible =
        !isDialogActive &&
        !isEndingShowing &&
        !isSplashVisible &&
        !isInventoryDisplaying &&
        !isTutorialOpen;

      if (shouldInteractBtnBeVisible) {
        // Check item interaction
        if (closestItem?.canInteract(playerPosition)) {
          interactionAvailable = true;
        }
        // Check NPC interaction (only if no item found yet, though logic effectively prioritizes item)
        else if (closestNPC?.canInteract(playerPosition)) {
          interactionAvailable = true;
        }
      }

      this.inputManager.setInteractButtonVisible(shouldInteractBtnBeVisible);
      this.inputManager.setInteractButtonActive(interactionAvailable);

      if (isEndingShowing || isSplashVisible || isTutorialOpen) {
        this.inputManager.setMobileControlsVisible(false);
      }

      // Check for collision-triggered dialogs (only if dialog is not already active)
      if (!this.dialogSystem.isActive() && !isTutorialOpen) {
        const npcs = this.storyManager.getNPCs();
        for (const npc of npcs) {
          // Check if NPC should trigger on collision (NPC handles the "just entered" logic)
          if (npc.shouldTriggerOnCollision()) {
            console.log(`DBX Collision dialog triggered for NPC: ${npc.name}`);
            // Mark collision as triggered so it won't happen again
            npc.markCollisionTriggered();

            // Trigger the dialog
            const interactionResult = npc.interact();
            if (interactionResult) {
              if (
                typeof interactionResult === 'object' &&
                'type' in interactionResult &&
                interactionResult.type === 'repeat'
              ) {
                // Show simple repeat dialog with images
                this.dialogSystem
                  .showSimpleDialog(
                    interactionResult.speaker,
                    interactionResult.text,
                    npc.name,
                    interactionResult.characterImage,
                    interactionResult.backgroundImage,
                    interactionResult.isEnd,
                  )
                  .catch(error => {
                    console.error('Failed to show simple dialog:', error);
                  });
              } else {
                // Show full dialog tree for first interaction
                this.dialogSystem
                  .showDialogTree(interactionResult as DialogTree, npc.name)
                  .catch(error => {
                    console.error('Failed to show dialog tree:', error);
                  });
              }
            }
            break; // Only trigger one dialog at a time
          }
        }
      }

      // Check for collider visibility toggle
      if (this.inputManager.wasToggleCollidersPressed() && !isTutorialOpen) {
        this.toggleAllColliders();
      }

      // Check for inventory toggle
      if (this.inputManager.wasToggleInventoryPressed() && !isTutorialOpen) {
        this.inventoryDisplay.toggle();
      }

      // Control mobile UI visibility based on dialog state
      const isDialogSystemActive = this.dialogSystem.isActive();

      const shouldMobileControlVisible =
        !isDialogSystemActive && !isInventoryDisplaying && !isTutorialOpen;

      if (shouldMobileControlVisible) {
        // Hide all mobile controls during dialog
        this.inputManager.setMobileControlsVisible(true);
      } else {
        // Show mobile controls when not in dialog
        this.inputManager.setMobileControlsVisible(false);
      }

      const shouldInventoryBeVisible =
        !isDialogActive && !isEndingShowing && !isSplashVisible && !isTutorialOpen;
      this.inventoryDisplay.setVisible(shouldInventoryBeVisible);

      // Handle interaction
      if (this.inputManager.wasInteractPressed() && !isTutorialOpen) {
        if (!this.dialogSystem.isActive()) {
          // Check for item interaction first (items have priority if player is near both item and NPC)
          if (closestItem?.canInteract(playerPosition)) {
            this.handleItemPickup(closestItem);
          } else {
            // Use StoryManager to handle NPC interaction
            const interactionResult = this.storyManager.handleNPCInteraction(playerPosition);

            if (interactionResult) {
              if (
                typeof interactionResult === 'object' &&
                'type' in interactionResult &&
                interactionResult.type === 'repeat'
              ) {

                // Show simple repeat dialog with images
                this.dialogSystem
                  .showSimpleDialog(
                    interactionResult.speaker,
                    interactionResult.text,
                    closestNPC?.name || 'Unknown',
                    interactionResult.characterImage,
                    interactionResult.backgroundImage,
                    interactionResult.isEnd,
                  )
                  .catch(error => {
                    console.error('Failed to show simple dialog:', error);
                  });
              } else {
                // Show full dialog tree for first interaction
                // Use async call but don't await to avoid blocking the game loop
                // this.soundManager.play('sfx_dialogOpen');
                this.dialogSystem
                  .showDialogTree(interactionResult as DialogTree, closestNPC?.name || 'Unknown')
                  .catch(error => {
                    console.error('Failed to show dialog tree:', error);
                  });
              }
            }
          }
        } else {
          // Handle dialog navigation/progression
          const cancelPressed = this.inputManager.wasCancelPressed();
          // Use async call but don't await to avoid blocking the game loop
          this.dialogSystem.handleInput(false, false, true, cancelPressed).catch(error => {
            console.error('Failed to handle dialog input:', error);
          });
        }
      }

      // Handle dialog navigation when dialog is active
      if (this.dialogSystem.isActive() && !isTutorialOpen) {
        const upPressed = this.inputManager.wasUpPressed();
        const downPressed = this.inputManager.wasDownPressed();
        const cancelPressed = this.inputManager.wasCancelPressed();
        if (upPressed || downPressed || cancelPressed) {
          // Use async call but don't await to avoid blocking the game loop
          this.dialogSystem
            .handleInput(upPressed, downPressed, false, cancelPressed)
            .catch(error => {
              console.error('Failed to handle dialog input:', error);
            });
        }
      }

      // Update dialog system
      this.dialogSystem.update();

      // Update camera to follow player
      this.camera.setTarget(this.player.getPosition());
      this.camera.update();
    } else {
      this.inputManager.setMobileControlsVisible(false);
      this.inputManager.setInteractButtonVisible(false);
    }
  }

  public destroy(): void {
    this.soundManager.destroy();
    this.app.destroy(true);
  }

  /**
   * Show the ending screen with the specified type and message
   */
  public showEndingScreen(type: 'victory' | 'gameOver', title: string, caption: string): void {
    // Hide all UI elements
    this.dayDisplay.hide();
    this.volumeControl.hide();
    this.inventoryDisplay.hide();
    this.affectionDisplay.hide();

    InputManager.getInstance().setMobileControlsVisible(false);
    InputManager.getInstance().setInteractButtonVisible(false);

    const score = this.storyManager.getTotalPoints();
    const gameData = {
      source: 'my-pixi-game',
      type: 'GAME_OVER',
      payload: {
        score: score,
        status: type,
        title: title,
        timestamp: Date.now(),
        // Stats untuk React Wrapper's Results screen
        duration: Math.floor(performance.now() / 1000), // convert to seconds
        itemCount: this.storyManager.getDiscoveredItems().size,
        affinityScore: this.storyManager.getAffection(),
        secretCount: this.storyManager.getSpecialDialogCount(),
        endingScore: score,
        finalPersonality: this.storyManager.getAffection() >= this.storyManager.getHostility() ? 'Friendly' : 'Reserved',
        finalRank: score >= 100 ? 'S' : score >= 70 ? 'A' : score >= 40 ? 'B' : score >= 0 ? 'C' : 'F',
        ending_number: 1
      }
    };

    if (window.parent !== window) {
      window.parent.postMessage(gameData, '*');
      console.log('🏁 GAME_OVER postMessage sent to wrapper with full stats:', gameData);
    }

    this.endingScreen.show({
      type,
      title,
      caption,
      onRestart: () => this.restartGame(),
    });
  }

  /**
   * Restart the game by refreshing the page
   */
  private restartGame(): void {
    console.log('Restarting game...');

    // Stop all sounds before restarting
    this.soundManager.stopAll();

    // Simply refresh the page to restart
    window.location.reload();
  }

  /**
   * Update NPCs when the day changes
   */
  private updateNPCsForNewDay(): void {
    // Remove existing NPCs from camera
    for (const npc of this.currentNPCs) {
      this.camera.container.removeChild(npc.sprite);
      this.camera.container.removeChild(npc.uiContainer);
    }

    // Get new NPCs from StoryManager (which has already been updated for the new day)
    this.currentNPCs = this.storyManager.getNPCs();

    // Add new NPCs to camera
    for (const npc of this.currentNPCs) {
      // NPCs have lower z-index than player (1 vs 2) so player renders on top
      this.camera.addToWorld(npc.sprite, 1);
      // Add NPC UI container at high z-index so name text and speech bubble render above world elements
      this.camera.addToWorld(npc.uiContainer, 100);
    }

    console.log(`Updated ${this.currentNPCs.length} NPCs for the new day`);
  }

  /**
   * Toggle visibility of NPC colliders for debugging purposes
   */
  public toggleNPCColliders(visible: boolean): void {
    this.storyManager.setNPCCollidersVisible(visible);
  }

  /**
   * Toggle visibility of all colliders (world objects, NPCs, polygon colliders, and player)
   */
  public toggleAllColliders(): void {
    this.collidersVisible = !this.collidersVisible;

    // Toggle world boundary colliders
    for (const collider of this.colliders) {
      collider.setVisible(this.collidersVisible);
    }

    // Toggle polygon colliders
    for (const polygonCollider of this.polygonColliders) {
      polygonCollider.setVisible(this.collidersVisible);
    }

    // Toggle NPC colliders
    this.storyManager.setNPCCollidersVisible(this.collidersVisible);

    // Toggle player collider
    this.player.setColliderVisible(this.collidersVisible);

    console.log(`Colliders ${this.collidersVisible ? 'shown' : 'hidden'}`);
  }

  /**
   * Get all NPCs (useful for debugging or other systems)
   */
  public getNPCs() {
    return this.storyManager.getNPCs();
  }

  /**
   * Get sound manager instance (useful for external sound control)
   */
  public getSoundManager(): SoundManager {
    return this.soundManager;
  }

  /**
   * Toggle sound mute state
   */
  public toggleSoundMute(): boolean {
    return this.soundManager.toggleMute();
  }

  /**
   * Set master volume
   */
  public setSoundVolume(volume: number): void {
    this.soundManager.setMasterVolume(volume);
  }

  /**
   * Get current day information
   */
  public getCurrentDay(): number {
    return this.storyManager.getCurrentDay();
  }

  /**
   * Advance to the next day
   */
  public nextDay(howManyDays?: number): boolean {
    return this.storyManager.nextDay(howManyDays);
  }

  /**
   * Go back to the previous day
   */
  public previousDay(): boolean {
    return this.storyManager.previousDay();
  }

  /**
   * Set a specific day
   */
  public setDay(day: number): void {
    this.storyManager.setDay(day);
  }

  /**
   * Get information about a specific day
   */
  public getDayInfo(day?: number) {
    return this.storyManager.getDayInfo(day);
  }
}
